import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
  alpha,
} from '@material-ui/core';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import { useIsGuest } from '../guest';
import { useWikiSections } from '../wiki/useWikiSections';
import { guestTechDocsAddonsPlugin } from './guestTechDocsAddonsPlugin';

const useStyles = makeStyles(theme => ({
  bar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(0.5, 3),
  },
  field: { marginTop: theme.spacing(2) },
  error: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: alpha(theme.palette.error.main, 0.06),
  },
  ok: {
    padding: theme.spacing(1, 1.5),
    borderLeft: `4px solid ${theme.palette.success.main}`,
    backgroundColor: alpha(theme.palette.success.main, 0.06),
  },
  warn: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: alpha(theme.palette.warning.main, 0.08),
  },
  mono: { fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' },
}));

interface MoveResult {
  moved: { from: string; to: string };
  sameRepo: boolean;
  destProject: string;
  destCommitUrl?: string;
  sourceCommitUrl?: string;
  docsPath: string;
  nav: string;
  bootstrapped: string[];
  catalogInfoUrl?: string;
  references: string[];
  referencesChecked: boolean;
  relativeLinks: string[];
}

const repoOf = (annotations: Record<string, string> = {}) => {
  const raw =
    [
      annotations['backstage.io/source-location'],
      annotations['backstage.io/managed-by-location'],
      annotations['backstage.io/techdocs-ref'],
    ]
      .filter(Boolean)
      .find(a => a.startsWith('url:')) ?? '';
  return raw
    .replace(/^url:/, '')
    .replace(/\/-\/.*$/, '')
    .replace(/\.git$/, '')
    .replace(/\/+$/, '');
};

const MoveWikiPageAddon = () => {
  const classes = useStyles();
  const isGuest = useIsGuest();
  const { pathname } = useLocation();
  const { entityRef } = useTechDocsReaderPage();
  const catalog = useApi(catalogApiRef);
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const { sections, wikiRepos } = useWikiSections();

  const [sourceRepo, setSourceRepo] = useState<string>();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('');
  const [customSection, setCustomSection] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [repoPath, setRepoPath] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<MoveResult>();

  useEffect(() => {
    let cancelled = false;
    catalog
      .getEntityByRef(entityRef)
      .then(e => {
        if (!cancelled) setSourceRepo(repoOf(e?.metadata.annotations));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [catalog, entityRef]);

  const pagePath = (() => {
    const rel = pathname
      .replace(/^\/docs\/[^/]+\/[^/]+\/[^/]+\/?/, '')
      .replace(/\/+$/, '');
    try {
      return decodeURIComponent(rel);
    } catch {
      return rel;
    }
  })();
  const isWiki =
    !!sourceRepo &&
    !!wikiRepos?.some(r => r.replace(/\/+$/, '') === sourceRepo);
  const movable = pagePath !== '' && !/(^|\/)index$/.test(pagePath);

  if (isGuest || !isWiki || !movable) return null;

  const section = advanced ? customSection.trim() : choice;
  const canMove =
    !running &&
    !result &&
    !!section &&
    (!advanced || /^https:\/\//.test(repoPath.trim()));

  const move = async () => {
    setRunning(true);
    setError(undefined);
    try {
      const base = await discovery.getBaseUrl('wiki-import');
      const r = await fetch(`${base}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceRepo,
          pagePath,
          section,
          sectionTitle: section,
          repoPath: advanced ? repoPath.trim() : undefined,
          overwrite,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message ?? `Error ${r.status}`);
      setResult(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const close = () => {
    setOpen(false);
    setResult(undefined);
    setError(undefined);
  };

  return (
    <div className={classes.bar}>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<CompareArrowsIcon />}
        onClick={() => setOpen(true)}
      >
        Move page
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Move this page</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Current page: <span className={classes.mono}>{pagePath}</span>. It
            is moved with direct commits to develop: it is removed from the
            current repository (including its menu and index) and added to the
            destination.
          </Typography>

          {!result && (
            <>
              {!advanced && (
                <TextField
                  select
                  fullWidth
                  label="Destination section"
                  value={choice}
                  onChange={e => setChoice(e.target.value)}
                  className={classes.field}
                >
                  {sections.map(s => (
                    <MenuItem key={s.name} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <FormControlLabel
                className={classes.field}
                control={
                  <Checkbox
                    checked={advanced}
                    onChange={e => setAdvanced(e.target.checked)}
                  />
                }
                label="Use another GitLab repository"
              />
              {advanced && (
                <>
                  <TextField
                    fullWidth
                    label="Repository URL"
                    placeholder="https://gitlab.afbaws.com/group/project"
                    value={repoPath}
                    onChange={e => setRepoPath(e.target.value)}
                    className={classes.field}
                  />
                  <TextField
                    fullWidth
                    label="Destination section"
                    helperText="Folder in that repository (docs/<section>/); it is created if it does not exist."
                    value={customSection}
                    onChange={e => setCustomSection(e.target.value)}
                    className={classes.field}
                  />
                </>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={overwrite}
                    onChange={e => setOverwrite(e.target.checked)}
                  />
                }
                label="Overwrite if it already exists at the destination"
              />
            </>
          )}

          {error && (
            <div className={classes.error} role="alert">
              <Typography variant="body2">{error}</Typography>
            </div>
          )}

          {result && (
            <div role="status" aria-live="polite">
              <div className={classes.ok}>
                <Typography variant="subtitle2">Page moved.</Typography>
                <Typography variant="body2" className={classes.mono}>
                  {result.moved.from} → {result.destProject}/{result.moved.to}
                </Typography>
                <Typography variant="body2">{result.nav}</Typography>
                <Typography variant="body2">
                  TechDocs regenerates it on the next visit (this may take a few
                  seconds).
                </Typography>
                <Typography variant="body2">
                  <Link to={result.docsPath}>Open in the new location</Link>
                  {result.destCommitUrl && (
                    <>
                      {' · '}
                      <a
                        href={result.destCommitUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Commit at the destination
                      </a>
                    </>
                  )}
                  {!result.sameRepo && result.sourceCommitUrl && (
                    <>
                      {' · '}
                      <a
                        href={result.sourceCommitUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Commit at the source
                      </a>
                    </>
                  )}
                </Typography>
              </div>
              {result.bootstrapped.length > 0 && (
                <div className={classes.warn}>
                  <Typography variant="body2">
                    The destination repository was new: the commit also created{' '}
                    {result.bootstrapped.join(', ')}. Register the component in
                    the catalog with{' '}
                    <span className={classes.mono}>
                      {result.catalogInfoUrl}
                    </span>
                    .
                  </Typography>
                </div>
              )}
              {result.references.length > 0 && (
                <div className={classes.warn}>
                  <Typography variant="subtitle2">
                    Other pages in the source mention this one (check their
                    links):
                  </Typography>
                  <ul>
                    {result.references.map(r => (
                      <li key={r} className={classes.mono}>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!result.referencesChecked && (
                <div className={classes.warn}>
                  <Typography variant="body2">
                    Could not check which pages linked to this one.
                  </Typography>
                </div>
              )}
              {result.relativeLinks.length > 0 && (
                <div className={classes.warn}>
                  <Typography variant="subtitle2">
                    This page has relative links that may break in the new
                    location:
                  </Typography>
                  <ul>
                    {result.relativeLinks.map(r => (
                      <li key={r} className={classes.mono}>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button
              color="primary"
              variant="contained"
              disabled={!canMove}
              onClick={move}
              startIcon={
                running ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {running ? 'Moving…' : 'Move'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const MoveWikiPage = guestTechDocsAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'MoveWikiPage',
    location: TechDocsAddonLocations.Subheader,
    component: MoveWikiPageAddon,
  }),
);
